import { Box, Button, useTheme, Stack, IconButton } from '@mui/material';
import SettingsVoiceIcon from '@mui/icons-material/SettingsVoice';
import Form from 'components/Form';
import useI18n from 'hooks/useI18n';
import { useState, useEffect, useRef } from 'react';
import { Close } from '@mui/icons-material';
import {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import styled from 'styled-components';
import useUser from 'store/user/useUser';

declare const PuzzleSDK: any;

interface Props {
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  disabled?: boolean;
  isTestMode?: boolean;
}

function NarrativeRecord({
  register,
  setValue,
  watch,
  disabled,
  isTestMode,
}: Props) {
  const i18n = useI18n();
  const { palette } = useTheme();
  const [voiceOn, setVoiceOn] = useState<boolean>(false);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const puzzleWsRef = useRef<any>(null);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
  const { student_id } = useUser();

  // 음성 인식 결과를 처리하는 콜백 함수
  const sttCallback = (stt: string) => {
    if (!stt) return;
  };

  // TextArea onChange 핸들러
  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue('narrativeNote', e.target.value, {
      shouldValidate: true,
    });
  };

  // textAreaRef의 값을 감시하는 useEffect
  useEffect(() => {
    const interval = setInterval(() => {
      if (
        textAreaRef.current &&
        textAreaRef.current.value !== watch('narrativeNote')
      ) {
        setValue('narrativeNote', textAreaRef.current.value, {
          shouldValidate: true,
        });
      }
    }, 100); // 100ms마다 체크

    return () => clearInterval(interval);
  }, [setValue, watch]);

  // WebSocket 연결 상태를 처리하는 콜백 함수
  const connectedCallback = (status: boolean) => {
    console.log('connectedCallback:', status);
    setIsConnected(status);
  };

  const initializePuzzleSDK = () => {
    try {
      if (!PuzzleSDK) {
        console.error('PuzzleSDK is not loaded');
        return false;
      }

      const uri = process.env.REACT_APP_PUZZLE_AI_WS_URL;
      const appKey = process.env.REACT_APP_PUZZLE_AI_APP_KEY;
      const userId = student_id;
      const modelLanguage = 'ENG';

      const fullUri = `${uri}?appKey=${encodeURIComponent(
        appKey || ''
      )}&userId=${encodeURIComponent(
        userId || ''
      )}&modelLanguage=${encodeURIComponent(modelLanguage)}`;

      PuzzleSDK.createInstance(fullUri, connectedCallback, sttCallback);

      return true;
    } catch (error) {
      console.error('Failed to initialize PuzzleSDK:', error);
      return false;
    }
  };

  const handleVoiceConnect = () => {
    try {
      if (!puzzleWsRef.current) {
        const initialized = initializePuzzleSDK();
        if (!initialized) return;

        puzzleWsRef.current = PuzzleSDK.getInstance();
        puzzleWsRef.current.addBoard('narrative', '서술기록');
        puzzleWsRef.current.addItem('narrative', 'narrativeNote', '서술');
      }

      puzzleWsRef.current.restartRecord();
      setIsConnected(true);
    } catch (error) {
      console.error('Failed to connect:', error);
      setIsConnected(false);
    }
  };

  const handleVoiceDisconnect = () => {
    try {
      if (puzzleWsRef.current) {
        puzzleWsRef.current.stopRecord();
        setIsConnected(false);
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to disconnect:', error);
    }
  };

  const handleVoiceRestart = () => {
    try {
      if (puzzleWsRef.current) {
        puzzleWsRef.current.stopRecord();
        setTimeout(() => {
          puzzleWsRef.current.restartRecord();
        }, 200);
      }
    } catch (error) {
      console.error('Failed to restart:', error);
    }
  };

  const VoiceMenuOnHandler = () => {
    setVoiceOn(true);
  };

  const VoiceMenuOFFHandler = () => {
    handleVoiceDisconnect();
    setVoiceOn(false);
  };

  const openManual = () => {
    window.open(
      'https://smartnurse.notion.site/262728473372808cb98ad58661b2631a?pvs=74',
      '_blank'
    );
  };

  const handlePreventEvent = (e: React.ClipboardEvent | React.MouseEvent) => {
    if (isTestMode) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  useEffect(() => {
    return () => {
      if (puzzleWsRef.current) {
        handleVoiceDisconnect();
      }
    };
  }, []);

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        <Button
          variant="contained"
          sx={{ minWidth: '15px', padding: '6px 7px' }}
          onClick={VoiceMenuOnHandler}
          disabled={disabled}
        >
          <SettingsVoiceIcon
            fontSize="small"
            sx={{
              color: 'white',
              height: '18px',
              animation: isConnected ? 'pulse 1.5s infinite' : 'none',
              '@keyframes pulse': {
                '0%': { opacity: 1 },
                '50%': { opacity: 0.5 },
                '100%': { opacity: 1 },
              },
            }}
          />
        </Button>
        {!voiceOn ? null : (
          <Box
            sx={{
              border: `1px solid ${palette.primary.main}`,
              borderRadius: '4px',
              marginLeft: '10px',
              display: 'flex',
              alignItems: 'center',
              height: '30px',
            }}
          >
            <Button
              sx={{ fontSize: '12px' }}
              onClick={handleVoiceConnect}
              disabled={isConnected}
            >
              開啟語音
            </Button>
            <Button
              sx={{ fontSize: '12px' }}
              onClick={handleVoiceRestart}
              disabled={!isConnected}
            >
              重新啟動
            </Button>
            <Button
              sx={{ fontSize: '12px' }}
              onClick={handleVoiceDisconnect}
              disabled={!isConnected}
            >
              關閉語音
            </Button>
            <Button sx={{ fontSize: '12px' }} onClick={openManual}>
              使用手冊
            </Button>

            <a
              href="https://puzzle-ai.com/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <div style={{ fontSize: '12px' }}>
                <span style={{ color: 'rgba(0, 0, 0, 0.7)' }}>Powered by</span>
                <span style={{ fontWeight: 'bold' }}> PuzzleAI</span>
              </div>
            </a>

            <IconButton size="small" onClick={VoiceMenuOFFHandler}>
              <Close color="primary" sx={{ height: '18px' }} />
            </IconButton>
          </Box>
        )}
      </Box>
      <Form.Item label={i18n('NARRATIVE.NARRATIVENOTE')}>
        <TextArea
          id="narrativeNote"
          ref={textAreaRef}
          defaultValue=""
          value={watch('narrativeNote')}
          onChange={handleTextAreaChange}
          aria-multiline="true"
          disabled={disabled}
          onPaste={handlePreventEvent}
          onCopy={handlePreventEvent}
          onContextMenu={handlePreventEvent}
          rows={20}
        />
      </Form.Item>
    </Stack>
  );
}

export default NarrativeRecord;

const TextArea = styled.textarea`
  width: 100%;
  height: 100%;
  padding: 10px;
  outline: none;
  border-radius: 4px;
  resize: none;
  font-size: 14px;
  font-weight: 400;
`;
